import { SessionType } from '@/types/model';
import { randomUuid } from '@/practice/crypto';

const STORAGE_KEY = 'issen-media-session-start-v1';
const MAX_CONFIRMED_TOKENS = 8;

export type SessionStartAttempt = {
  requestId: string;
  supersedesSessionId?: string;
  sessionOwnershipToken?: string;
};

type ConfirmedSessionToken = {
  sessionId: string;
  sessionOwnershipToken: string;
};

type StoredSessionStart = SessionStartAttempt & {
  ownerUserId: string;
  sessionType: SessionType;
};

export class SessionStartOwnership {
  private inMemoryRecord: StoredSessionStart | null = null;
  private confirmedTokens: ConfirmedSessionToken[] = [];

  constructor(private readonly storage: Storage | null) {}

  begin(ownerUserId: string, sessionType: SessionType): SessionStartAttempt {
    const existing = this.owned(ownerUserId, sessionType);
    if (existing === null) {
      return this.writePending(ownerUserId, sessionType, undefined, undefined);
    }
    if (
      existing.sessionOwnershipToken === undefined
      || existing.supersedesSessionId !== undefined
    ) {
      return existing;
    }
    this.rememberConfirmedToken(
      existing.requestId,
      existing.sessionOwnershipToken,
    );
    return this.writePending(
      ownerUserId,
      sessionType,
      existing.requestId,
      existing.sessionOwnershipToken,
    );
  }

  active(
    ownerUserId: string,
    sessionType: SessionType,
    sessionId: string,
  ): SessionStartAttempt | null {
    const existing = this.owned(ownerUserId, sessionType);
    if (existing === null) {
      return null;
    }
    const token = this.tokenFor(sessionId, existing);
    if (token === undefined) {
      return null;
    }
    if (
      existing.requestId === sessionId
      && existing.supersedesSessionId === undefined
      && existing.sessionOwnershipToken === token
    ) {
      return existing;
    }
    return {
      requestId: sessionId,
      sessionOwnershipToken: token,
    };
  }

  confirm(
    attempt: SessionStartAttempt,
    ownerUserId: string,
    sessionType: SessionType,
    sessionId: string,
    sessionOwnershipToken: string,
  ): void {
    const current = this.read();
    if (current === null) {
      return;
    }
    const matchesAttempt = current.requestId === attempt.requestId
      && current.supersedesSessionId === attempt.supersedesSessionId
      && current.sessionOwnershipToken === attempt.sessionOwnershipToken;
    const matchesFallbackSource = attempt.supersedesSessionId === undefined
      && attempt.sessionOwnershipToken !== undefined
      && (
        current.requestId === attempt.requestId
        || current.supersedesSessionId === attempt.requestId
      );
    if (!matchesAttempt && !matchesFallbackSource) {
      return;
    }
    this.rememberConfirmedToken(sessionId, sessionOwnershipToken);
    this.write({
      ownerUserId,
      sessionType,
      requestId: sessionId,
      sessionOwnershipToken,
    });
  }

  clear(): void {
    this.inMemoryRecord = null;
    this.confirmedTokens = [];
    this.storage?.removeItem(STORAGE_KEY);
  }

  private tokenFor(
    sessionId: string,
    existing: StoredSessionStart,
  ): string | undefined {
    if (
      existing.requestId === sessionId
      && existing.supersedesSessionId === undefined
      && existing.sessionOwnershipToken !== undefined
    ) {
      return existing.sessionOwnershipToken;
    }
    if (
      existing.supersedesSessionId === sessionId
      && existing.sessionOwnershipToken !== undefined
    ) {
      return existing.sessionOwnershipToken;
    }
    for (let i = this.confirmedTokens.length - 1; i >= 0; i -= 1) {
      if (this.confirmedTokens[i]?.sessionId === sessionId) {
        return this.confirmedTokens[i]?.sessionOwnershipToken;
      }
    }
    return undefined;
  }

  private rememberConfirmedToken(
    sessionId: string,
    sessionOwnershipToken: string,
  ): void {
    this.confirmedTokens = this.confirmedTokens.filter(
      (token) => token.sessionId !== sessionId,
    );
    this.confirmedTokens.push({ sessionId, sessionOwnershipToken });
    if (this.confirmedTokens.length > MAX_CONFIRMED_TOKENS) {
      this.confirmedTokens = this.confirmedTokens.slice(
        this.confirmedTokens.length - MAX_CONFIRMED_TOKENS,
      );
    }
  }

  private parseConfirmedTokens(value: unknown): ConfirmedSessionToken[] {
    if (!Array.isArray(value)) {
      return [];
    }
    const parsed: ConfirmedSessionToken[] = [];
    for (const entry of value) {
      if (entry === null || typeof entry !== 'object') {
        continue;
      }
      const record = entry as Partial<ConfirmedSessionToken>;
      if (
        typeof record.sessionId !== 'string'
        || typeof record.sessionOwnershipToken !== 'string'
      ) {
        continue;
      }
      parsed.push({
        sessionId: record.sessionId,
        sessionOwnershipToken: record.sessionOwnershipToken,
      });
    }
    return parsed;
  }

  private writePending(
    ownerUserId: string,
    sessionType: SessionType,
    supersedesSessionId: string | undefined,
    sessionOwnershipToken: string | undefined,
  ): StoredSessionStart {
    return this.write({
      ownerUserId,
      sessionType,
      requestId: randomUuid().replace(/-/g, ''),
      supersedesSessionId,
      sessionOwnershipToken,
    });
  }

  private owned(
    ownerUserId: string,
    sessionType: SessionType,
  ): StoredSessionStart | null {
    const record = this.read();
    return (
      record !== null
      && record.ownerUserId === ownerUserId
      && record.sessionType === sessionType
    ) ? record : null;
  }

  private read(): StoredSessionStart | null {
    const serialized = this.storage?.getItem(STORAGE_KEY);
    if (serialized === null || serialized === undefined) {
      return this.inMemoryRecord;
    }
    try {
      const value = JSON.parse(serialized) as Partial<StoredSessionStart> & {
        confirmedTokens?: unknown;
      };
      if (
        typeof value.ownerUserId !== 'string'
        || typeof value.sessionType !== 'string'
        || typeof value.requestId !== 'string'
        || (
          value.supersedesSessionId !== undefined
          && typeof value.supersedesSessionId !== 'string'
        )
        || (
          value.sessionOwnershipToken !== undefined
          && typeof value.sessionOwnershipToken !== 'string'
        )
      ) {
        return null;
      }
      this.confirmedTokens = this.parseConfirmedTokens(value.confirmedTokens);
      const record: StoredSessionStart = {
        ownerUserId: value.ownerUserId,
        sessionType: value.sessionType as SessionType,
        requestId: value.requestId,
      };
      if (value.supersedesSessionId !== undefined) {
        record.supersedesSessionId = value.supersedesSessionId;
      }
      if (value.sessionOwnershipToken !== undefined) {
        record.sessionOwnershipToken = value.sessionOwnershipToken;
      }
      this.inMemoryRecord = record;
      return this.inMemoryRecord;
    } catch {
      return null;
    }
  }

  private write(record: StoredSessionStart): StoredSessionStart {
    this.inMemoryRecord = record;
    this.storage?.setItem(STORAGE_KEY, JSON.stringify({
      ...record,
      confirmedTokens: this.confirmedTokens,
    }));
    return record;
  }
}
